import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  ShieldCheck,
  Truck,
  IndianRupee,
  MessageCircle,
  Sparkles,
  Star,
  Instagram,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, mediaUrl } from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";
import { toast } from "sonner";

function Section({ id, overline, title, subtitle, right, children }) {
  return (
    <section
      id={id}
      className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24"
    >
      <div className="flex items-end justify-between gap-4 mb-8 md:mb-12">
        <div>
          {overline && (
            <div className="overline text-[#E63946] mb-3">{overline}</div>
          )}
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tighter">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-3 text-white/60 max-w-xl">{subtitle}</p>
          )}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function EmptyProducts({
  hint = "Products will appear here once the admin adds them.",
}) {
  return (
    <div className="border border-dashed border-white/15 py-12 px-6 text-center">
      <Sparkles size={22} className="mx-auto text-[#E63946] mb-3" />
      <div className="font-display text-xl mb-1">Coming Soon</div>
      <p className="text-sm text-white/50 max-w-md mx-auto">
        {hint} Visit the{" "}
        <Link to="/admin" className="text-[#E63946] underline">
          admin panel
        </Link>{" "}
        to add products.
      </p>
    </div>
  );
}

export default function Home() {
  const { settings, categories } = useApp();
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { tag: "best-seller", limit: 8 } })
      .then((r) => setBestSellers(r.data.items || []));
    api
      .get("/products", { params: { sort: "newest", limit: 8 } })
      .then((r) => setNewArrivals(r.data.items || []));
    api
      .get("/products", { params: { tag: "deal", limit: 8 } })
      .then((r) => setDeals(r.data.items || []));
    api
      .get("/reviews", { params: { limit: 6 } })
      .then((r) => setReviews(r.data.items || []));
    api
      .get("/builds", { params: { limit: 3, featured_only: true } })
      .then((r) => setBuilds(r.data.items || []));
    api.get("/faq").then((r) => setFaqs(r.data.items || []));
  }, []);

  const s = settings || {};

  const submitNewsletter = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Welcome to the driver's seat.");
      e.target.reset();
    } catch (err) {
      toast.error("Please enter a valid email.");
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[86vh] md:min-h-[92vh] overflow-hidden"
      >
        {s.hero_image && (
          <img
            src={s.hero_image}
            alt="Street Auto Hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-24 md:pt-40 pb-40 md:pb-24 h-full flex flex-col justify-end">
          <div className="max-w-3xl fade-up">
            <div className="overline text-[#E63946] mb-6">
              {s.tagline || "Modify, Personalize, Elevate"}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              {s.hero_heading_1 || "MODIFY."}
              <br />
              {s.hero_heading_2 || "PERSONALIZE."}
              <br />
              <span className="text-[#E63946]">
                {s.hero_heading_3 || "ELEVATE."}
              </span>
            </h1>
            <p className="mt-6 text-white/70 max-w-lg text-base md:text-lg">
              {s.hero_subtext}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={s.hero_cta_primary_url || "/shop"}
                className="sa-btn sa-btn-primary"
                data-testid="hero-cta-primary"
              >
                {s.hero_cta_primary || "SHOP NOW"} <ArrowRight size={16} />
              </Link>
              <Link
                to={s.hero_cta_secondary_url || "/#categories"}
                className="sa-btn sa-btn-outline"
                data-testid="hero-cta-secondary"
              >
                {s.hero_cta_secondary || "EXPLORE CATEGORIES"}
              </Link>
            </div>
          </div>
        </div>
        {/* corner marker */}
        <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-10 overline text-white/40 hidden md:block">
          MUMBAI · INDIA <span className="text-[#E63946] ml-2">●</span>
        </div>
      </section>

      {/* COMPLETE CAR TRANSFORMATION */}
      <section
        className="relative overflow-hidden border-y border-white/10 bg-[#111]"
        data-testid="transformation-promo"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(230,57,70,.22),transparent_42%)]" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-[1fr_auto] gap-8 items-end">
          <div>
            <div className="overline text-[#E63946] mb-4">
              Complete Car Transformation
            </div>
            <h2 className="font-display text-4xl md:text-7xl leading-[.9] max-w-3xl">
              FROM STOCK TO
              <br />
              <span className="text-[#E63946]">SERIOUSLY CUSTOM.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-xl">
              Give us your car. Tell us your vision. We'll help turn it into a
              complete build, from styling and interiors to performance and ECU
              remapping where appropriate.
            </p>
            <p className="text-xs text-white/40 mt-4 max-w-xl">
              All work is subject to vehicle suitability, technical assessment
              and applicable laws and regulations.
            </p>
          </div>
          <Link to="/build" className="sa-btn sa-btn-primary shrink-0">
            START YOUR BUILD <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* OUR BUILDS */}
      <Section
        overline="Our Builds"
        title="REAL CARS. REAL TRANSFORMATIONS."
        subtitle="Explore previous projects and find the direction for your own build."
        right={
          <Link
            to="/builds"
            className="hidden md:inline-flex overline text-white/70 items-center gap-2"
          >
            VIEW ALL BUILDS <ArrowRight size={14} />
          </Link>
        }
      >
        {builds.length ? (
          <div className="grid md:grid-cols-3 gap-5">
            {builds.map((b) => (
              <Link
                key={b.id}
                to={`/builds/${b.id}`}
                className="group relative aspect-[4/3] overflow-hidden border border-white/10"
              >
                <img
                  src={mediaUrl(b.cover_image || b.images?.[0]?.url)}
                  alt={`${b.make} ${b.model}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <div className="overline text-[#E63946]">
                    {b.title || "Complete Transformation"}
                  </div>
                  <div className="font-display text-2xl mt-1">
                    {b.make} {b.model}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/15 py-12 text-center text-white/50">
            Our latest transformations will appear here.
          </div>
        )}
      </Section>

      {/* SHOP BY CATEGORY (bento) */}
      <Section
        id="categories"
        overline="Shop By Category"
        title="EXPLORE THE CATALOGUE"
        subtitle="Curated categories designed to make finding the right upgrade effortless."
      >
        <div
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
          data-testid="category-grid"
        >
          {categories.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/collection/${c.slug}`}
              className={`group relative overflow-hidden border border-white/10 ${idx === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto md:min-h-[420px]" : "aspect-[4/5]"}`}
              data-testid={`category-${c.slug}`}
            >
              <img
                src={c.image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                <div className="overline text-[#E63946] mb-2">0{idx + 1}</div>
                <div className="font-display text-2xl md:text-3xl tracking-tighter">
                  {c.name}
                </div>
                <p className="text-xs text-white/60 mt-1 line-clamp-2 max-w-xs">
                  {c.description}
                </p>
                <div className="mt-4 overline text-white/80 flex items-center gap-2">
                  SHOP <ChevronRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* BEST SELLERS */}
      <Section
        overline="Best Sellers"
        title="POPULAR UPGRADES"
        subtitle="Popular upgrades chosen by fellow car enthusiasts."
        right={
          <Link
            to="/collection/best-sellers"
            className="hidden md:inline-flex overline text-white/70 hover:text-white items-center gap-2"
          >
            VIEW ALL <ArrowRight size={14} />
          </Link>
        }
      >
        {bestSellers.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {bestSellers.map((p) => (
              <ProductCard key={p.id} product={p} testIdPrefix="best" />
            ))}
          </div>
        ) : (
          <EmptyProducts />
        )}
      </Section>

      {/* NEW ARRIVALS */}
      <Section
        overline="New Arrivals"
        title="FRESH DROPS"
        subtitle="Fresh products. New possibilities for your drive."
        right={
          <Link
            to="/collection/new-arrivals"
            className="hidden md:inline-flex overline text-white/70 hover:text-white items-center gap-2"
          >
            VIEW ALL <ArrowRight size={14} />
          </Link>
        }
      >
        {newArrivals.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {newArrivals.map((p) => (
              <ProductCard key={p.id} product={p} testIdPrefix="new" />
            ))}
          </div>
        ) : (
          <EmptyProducts />
        )}
      </Section>

      {/* PROMO BANNER */}
      <section
        className="relative h-[420px] md:h-[540px] overflow-hidden"
        data-testid="promo-banner"
      >
        {s.promo_image && (
          <img
            src={s.promo_image}
            alt="Promo"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-l from-black/40 via-black/60 to-black/90" />
        <div className="absolute inset-0 max-w-[1400px] mx-auto px-4 md:px-8 flex items-center">
          <div className="max-w-xl">
            <div className="overline text-[#E63946] mb-5">
              STREET AUTO EXCLUSIVE
            </div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              {s.promo_heading}
            </h3>
            <p className="mt-4 text-white/70 text-lg">{s.promo_subtext}</p>
            <Link
              to={s.promo_cta_url || "/shop"}
              className="sa-btn sa-btn-primary mt-8"
              data-testid="promo-cta"
            >
              {s.promo_cta || "EXPLORE ACCESSORIES"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ASK EXPERT */}
      <Section overline="Need Guidance?" title="ASK AN EXPERT">
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          <div className="relative min-h-[280px] overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Expert"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30" />
          </div>
          <div className="bg-[#111] border border-white/10 p-8 md:p-10 flex flex-col justify-center">
            <div className="font-display text-3xl md:text-4xl leading-tight">
              Not Sure What Your Car Needs?{" "}
              <span className="text-[#E63946]">Ask Us.</span>
            </div>
            <p className="mt-4 text-white/60">
              Tell us what you're looking to modify, personalize or improve, and
              our team will help point you in the right direction.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/ask-expert"
                className="sa-btn sa-btn-primary"
                data-testid="ask-expert-cta"
              >
                ASK AN EXPERT
              </Link>
              <a
                href={`https://wa.me/91${s.whatsapp || ""}`}
                target="_blank"
                rel="noreferrer"
                className="sa-btn sa-btn-outline"
              >
                <MessageCircle size={16} /> WHATSAPP US
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* WHY US */}
      <Section overline="Why Street Auto Accessories" title="BUILT AROUND YOU">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-6">
          {[
            {
              icon: ShieldCheck,
              title: "Quality Products",
              desc: "Carefully selected automotive accessories.",
            },
            {
              icon: IndianRupee,
              title: "Affordable Prices",
              desc: "Products for different budgets.",
            },
            {
              icon: Truck,
              title: "Pan-India Delivery",
              desc: "We deliver across India.",
            },
            {
              icon: Sparkles,
              title: "Easy Shopping",
              desc: "Simple browsing and online ordering.",
            },
            {
              icon: MessageCircle,
              title: "Expert Assistance",
              desc: "Need help choosing? Ask us.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="sa-card p-5 md:p-6"
              data-testid={`why-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <f.icon size={22} className="text-[#E63946] mb-3" />
              <div className="font-display text-lg leading-tight">
                {f.title}
              </div>
              <div className="text-xs text-white/60 mt-2">{f.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* DEALS */}
      <Section
        overline="Deals"
        title="LIMITED-TIME OFFERS"
        right={
          <Link
            to="/collection/deals"
            className="hidden md:inline-flex overline text-white/70 hover:text-white items-center gap-2"
          >
            VIEW ALL <ArrowRight size={14} />
          </Link>
        }
      >
        {deals.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {deals.map((p) => (
              <ProductCard key={p.id} product={p} testIdPrefix="deal" />
            ))}
          </div>
        ) : (
          <EmptyProducts hint="Deals will appear here when the admin tags products with 'deal'." />
        )}
      </Section>

      {/* REVIEWS */}
      <Section overline="What Our Customers Say" title="TRUSTED BY DRIVERS">
        {reviews.length ? (
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="sa-card p-6"
                data-testid={`review-${r.id}`}
              >
                <div className="flex gap-1 mb-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      size={14}
                      fill={i < r.rating ? "#E63946" : "transparent"}
                      stroke="#E63946"
                    />
                  ))}
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  "{r.comment}"
                </p>
                <div className="mt-4 overline text-white/50">
                  — {r.customer_name}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/15 py-12 px-6 text-center">
            <div className="font-display text-xl mb-2">
              Be the first to review
            </div>
            <p className="text-sm text-white/50 max-w-md mx-auto">
              Once you've received your order, share your experience and help
              other drivers choose confidently.
            </p>
          </div>
        )}
      </Section>

      {faqs.length > 0 && (
        <Section
          overline="FAQ"
          title="ANSWERS FOR DRIVERS"
          subtitle="Advice from our team on the questions we hear most."
        >
          <div className="border-t border-white/10">
            {faqs.map((item) => (
              <details key={item.id} className="group border-b border-white/10">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 font-display text-lg md:text-xl">
                  {item.question}
                  <ChevronRight
                    size={18}
                    className="shrink-0 text-[#E63946] transition-transform group-open:rotate-90"
                  />
                </summary>
                <p className="max-w-3xl pb-5 pr-8 text-sm leading-relaxed text-white/65 whitespace-pre-line">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </Section>
      )}

      {/* INSTAGRAM */}
      <Section overline="Instagram" title="FOLLOW THE STREET">
        <div className="flex items-center justify-between mb-6">
          <p className="text-white/60">
            Follow {s.instagram || "@StreetAutoAccessories"} for automotive
            inspiration, new products and offers.
          </p>
          <a
            href={`https://instagram.com/${(s.instagram || "").replace("@", "")}`}
            target="_blank"
            rel="noreferrer"
            className="sa-btn sa-btn-outline"
            data-testid="instagram-cta"
          >
            <Instagram size={16} /> @{(s.instagram || "").replace("@", "")}
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {[
            "https://images.unsplash.com/photo-1552519507-da3b142c6e3d",
            "https://images.unsplash.com/photo-1616761879141-f485e5fed5df",
            "https://images.unsplash.com/photo-1542377281-73d08e3a10aa",
            "https://images.unsplash.com/photo-1638475408149-022148cf76fd",
            "https://images.unsplash.com/photo-1698533188439-6154da06689e",
            "https://images.unsplash.com/photo-1705563666935-4d0a72709948",
          ].map((u, i) => (
            <a
              key={u}
              href="#"
              className="relative aspect-square overflow-hidden group border border-white/10"
            >
              <img
                src={`${u}?w=400&q=70`}
                alt=""
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <Instagram
                  size={20}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-white"
                />
              </div>
            </a>
          ))}
        </div>
      </Section>

      {/* NEWSLETTER */}
      <section
        className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24"
        data-testid="newsletter-section"
      >
        <div className="grid md:grid-cols-2 gap-6 items-center bg-[#111] border border-white/10 p-8 md:p-14">
          <div>
            <div className="overline text-[#E63946] mb-3">Newsletter</div>
            <div className="font-display text-3xl md:text-5xl tracking-tighter leading-[0.95]">
              STAY IN THE
              <br />
              DRIVER'S SEAT.
            </div>
            <p className="mt-4 text-white/60">
              Get new product drops, special offers and automotive inspiration.
            </p>
          </div>
          <form
            onSubmit={submitNewsletter}
            className="flex flex-col sm:flex-row gap-3"
          >
            <input
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              className="sa-input"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              className="sa-btn sa-btn-primary"
              data-testid="newsletter-submit"
            >
              SUBSCRIBE
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
